import React, { useState, useEffect } from 'react';
import { FaArrowUp, FaPlus, FaUsers, FaStore, FaLayerGroup, FaExclamationCircle, FaAd, FaImage } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    buyers: 0,
    sellers: 0,
    categories: 0,
    issues: 0
  });
  const [recentSellers, setRecentSellers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Buyers & Sellers (Port 2027)
      const resBuyers = await fetch('http://localhost:2027/api/admin/buyers');
      const resSellers = await fetch('http://localhost:2027/api/admin/sellers-activity');
      
      // 2. Fetch Categories (Port 2026)
      const resCats = await fetch('http://localhost:2026/api/categories');

      const buyersData = resBuyers.ok ? await resBuyers.json() : [];
      const sellersData = resSellers.ok ? await resSellers.json() : [];
      const catsData = resCats.ok ? await resCats.json() : [];

      // Recursive function to count all categories and subcategories
      const countAll = (list: any[]): number => {
        let count = list.length;
        list.forEach(cat => {
          if (cat.subCategories && cat.subCategories.length > 0) {
            count += countAll(cat.subCategories);
          }
        });
        return count;
      };

      setCounts({
        buyers: buyersData.length,
        sellers: sellersData.length,
        categories: countAll(catsData),
        issues: 0 // As requested
      });

      // Recent 4 sellers for the table
      setRecentSellers(sellersData.slice(0, 4));

    } catch (err) {
      console.error("Dashboard Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const cards = [
    { title: 'TOTAL BUYERS', value: counts.buyers, trend: '+12%', icon: <FaUsers />, path: '/frog-side/users' },
    { title: 'TOTAL SELLERS', value: counts.sellers, trend: '+5%', icon: <FaStore />, path: '/frog-side/users' },
    { title: 'TOTAL CATEGORIES', value: counts.categories, trend: 'Active', icon: <FaLayerGroup />, path: '/frog-side/category-list' },
    { title: 'PENDING ISSUES', value: counts.issues, trend: 'Stable', icon: <FaExclamationCircle />, path: '/frog-side/issues' },
  ];

  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">Dashboard</h2>
        <p className="text-gray-400 font-medium mt-1">Premium TradyFrog Management Console</p>
      </div>

      {/* Dynamic Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, i) => (
          <div 
            key={i} 
            onClick={() => navigate(card.path)}
            className="bg-white p-8 rounded-[30px] shadow-sm border border-gray-100 hover:shadow-xl hover:scale-[1.02] transition-all group cursor-pointer relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gray-50 group-hover:bg-[#006d5b] transition-colors"></div>
            
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-2">{card.title}</p>
                <h3 className="text-3xl font-black text-gray-900">
                  {loading ? "..." : card.value}
                </h3>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl text-gray-300 group-hover:text-[#006d5b] group-hover:bg-[#006d5b]/5 transition-all">
                 {card.icon}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded-md bg-[#006d5b] text-white font-bold`}>
                {card.trend}
              </span>
              <span className="text-[10px] text-gray-400 font-bold uppercase">Live Data</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Registered Sellers Table */}
        <div className="lg:col-span-2 bg-white rounded-[35px] p-10 shadow-sm border border-gray-100">
           <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-black text-gray-900">Recently Joined Sellers</h3>
              <button 
                onClick={() => navigate('/frog-side/users')}
                className="text-xs text-[#006d5b] font-black tracking-widest hover:bg-[#006d5b]/5 px-4 py-2 rounded-lg transition-all uppercase"
              >
                View Directory
              </button>
           </div>
           <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-[10px] text-gray-400 uppercase tracking-widest border-b border-gray-50">
                  <tr>
                    <th className="pb-5">Company Name</th>
                    <th className="pb-5">Owner</th>
                    <th className="pb-5">Verification</th>
                    <th className="pb-5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {recentSellers.length > 0 ? recentSellers.map((seller, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0 group hover:bg-gray-50/50 transition-colors">
                      <td className="py-5 text-gray-900 font-bold">{seller.companyName || "N/A"}</td>
                      <td className="py-5 text-gray-400 font-medium">{seller.username}</td>
                      <td className="py-5">
                        <span className={`px-3 py-1 ${seller.isVerified ? 'bg-emerald-50 text-[#006d5b]' : 'bg-gray-100 text-gray-400'} text-[10px] font-black rounded-lg uppercase`}>
                          {seller.isVerified ? "Verified" : "Pending"}
                        </span>
                      </td>
                      <td className="py-5 text-right">
                         <button onClick={() => navigate('/frog-side/users')} className="text-[#006d5b] hover:underline text-xs font-bold">Manage</button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={4} className="py-10 text-center text-gray-400 font-bold uppercase text-xs">No Recent Activity</td></tr>
                  )}
                </tbody>
              </table>
           </div>
        </div>

        {/* Updated Quick Actions */}
        <div className="bg-white rounded-[35px] p-10 shadow-sm border border-gray-100">
          <h3 className="text-xl font-black text-gray-900 mb-8">Management Tools</h3>
          <div className="space-y-3">
            {[
              { label: 'ADS Management', icon: <FaAd />, path: '/frog-side/ads/add' },
              { label: 'Banner Settings', icon: <FaImage />, path: '/frog-side/banners' },
              { label: 'System Reports', icon: <FaPlus />, path: '/frog-side/reports' },
            ].map((action, i) => (
              <button 
                key={i} 
                onClick={() => navigate(action.path)}
                className="w-full py-4 bg-gray-900 hover:bg-[#006d5b] text-white text-[11px] font-black rounded-2xl transition-all text-left px-8 flex items-center justify-between group shadow-lg shadow-gray-200"
              >
                {action.label.toUpperCase()}
                <span className="text-[#006d5b] group-hover:text-white transition-colors">
                  {action.icon}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-8 p-6 bg-[#006d5b]/5 rounded-[25px] border border-[#006d5b]/10 text-center">
             <p className="text-[10px] font-black text-[#006d5b] uppercase tracking-widest mb-1">Infrastructure</p>
             <p className="text-xs text-[#006d5b]/80 font-bold">API Gateway: Online</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;